import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  validateMicrosoftOAuthConfig,
  generateMicrosoftOAuthPopupUrl,
  MICROSOFT_OAUTH_ENDPOINTS
} from '../config/microsoftOAuth';
import { getUserTimezone } from '../utils/timezoneUtils';

const SESSION_KEY_FLOW = 'microsoft_oauth_flow';
const SESSION_KEY_STATE = 'microsoft_oauth_state';
const SESSION_KEY_USER_TYPE = 'microsoft_oauth_userType';
const SESSION_KEY_RETURN_TO = 'microsoft_oauth_returnTo';
const SESSION_KEY_RESULT = 'microsoft_oauth_result';

// Mirrors useAppleOAuth.shouldUseRedirectFlow. window.open in an iOS RN
// WebView is blocked or has no opener, so the popup-based postMessage flow
// never completes. The redirect flow stays inside the WebView and needs no
// native-side support.
let isIOSDevice = false;
const shouldUseRedirectFlow = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  isIOSDevice = /iPhone|iPod|iPad/.test(ua);
  if ((window as any).ReactNativeWebView) return true;
  if (isIOSDevice) return true;
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return true;
  return false;
};

interface MicrosoftUserInfo {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  userPrincipalName: string;
  mail: string;
  businessPhones: string[];
  jobTitle: string;
  officeLocation: string;
  preferredLanguage: string;
  mobilePhone: string;
}

export const useMicrosoftOAuth = () => {
  const { socialLogin } = useAuth();
  const userTypeRef = useRef<number>(1);
  const callbacksRef = useRef<{
    onSuccess?: (_message?: string) => void;
    onError?: (_error: string) => void;
  }>({});
  const expectedStateRef = useRef<string | null>(null);
  // Cleanup for the in-progress OAuth flow. Set from inside `microsoftLogin`,
  // called by `cancelOAuth`.
  const cleanupRef = useRef<(() => void) | null>(null);

  const handleMicrosoftSuccess = useCallback(async (credentialResponse: any) => {
    try {
      // Get user info from Microsoft Graph API using config endpoint
      const response = await fetch(MICROSOFT_OAUTH_ENDPOINTS.userInfo, {
        headers: {
          Authorization: `Bearer ${credentialResponse.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user info from Microsoft');
      }

      const userInfo: MicrosoftUserInfo = await response.json();

      // Prepare data for social login
      const socialData = {
        loginType: 'microsoft' as const,
        accessToken: credentialResponse.access_token,
        email: userInfo.mail || userInfo.userPrincipalName,
        name: userInfo.displayName || 'Microsoft User',
        picture: undefined, // Microsoft doesn't provide profile picture in basic scope
        firstName: userInfo.givenName || 'Microsoft',
        lastName: userInfo.surname || 'User',
        userType: userTypeRef.current, // Use stored userType
        timezone: getUserTimezone(),
      };


      // Call the social login function
      const result = await socialLogin(socialData);

      // Show success message based on backend response
      if (result.success) {
        console.log('Microsoft login successful');
        // Pass success message from backend if available
        const successMessage = (result as any).message || 'Account created successfully. Redirecting to dashboard...';
        callbacksRef.current.onSuccess?.(successMessage);
      } else {
        console.error('Microsoft login failed:', result.message);
        // Pass the actual backend error message to the modal
        callbacksRef.current.onError?.(result.error || 'Microsoft login failed');
      }

      return result;
    } catch (error) {
      console.error('Microsoft OAuth error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Microsoft login failed';
      callbacksRef.current.onError?.(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [socialLogin]);

  // Microsoft OAuth login function using popup
  const microsoftLogin = useCallback(() => {
    if (!validateMicrosoftOAuthConfig()) {
      throw new Error('Microsoft OAuth is not configured. Please set REACT_APP_MICROSOFT_CLIENT_ID in your .env file.');
    }

    const { url: authUrl, state } = generateMicrosoftOAuthPopupUrl();
    expectedStateRef.current = state;

    if (shouldUseRedirectFlow()) {
      try {
        sessionStorage.setItem(SESSION_KEY_FLOW, 'redirect');
        sessionStorage.setItem(SESSION_KEY_STATE, state);
        sessionStorage.setItem(SESSION_KEY_USER_TYPE, String(userTypeRef.current));
        sessionStorage.setItem(
          SESSION_KEY_RETURN_TO,
          window.location.pathname + window.location.search
        );

        // In the iOS RN WebView the redirect flow navigates away from this page,
        // so the popup/`popup.closed` cancellation path never runs. Instead the
        // native layer posts an OAUTH_CANCEL_ERROR message when the user taps the
        // Back button. Listen for it on both `window` and
        // `document` (RN dispatches the event on `document`), surface a
        // "cancelled" error, and detach both listeners so it only fires once.
        if (isIOSDevice) {
          const handleMessage = (event: MessageEvent) => {
            const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;
  
            if (data.type === 'OAUTH_CANCEL_ERROR') {
              callbacksRef.current.onError?.('Login was cancelled');
  
              window.removeEventListener('message', handleMessage);
              // @ts-ignore - RN dispatches the 'message' event on `document`, not `window`
              document.removeEventListener('message', handleMessage);
            }
          };
  
          window.addEventListener('message', handleMessage);
          // @ts-ignore - RN dispatches the 'message' event on `document`, not `window`
          document.addEventListener('message', handleMessage);
        }
      } catch {
        // sessionStorage may be unavailable in private mode; fall through to popup as a last resort.
      }
      window.location.assign(authUrl);
      return;
    }

    // Open popup window
    const popup = window.open(
      authUrl,
      'microsoft-login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }

    const isReactNativeWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
    let settled = false;

    const cleanup = () => {
      settled = true;
      if (checkClosed) clearInterval(checkClosed);
      try { popup.close(); } catch {console.log("Error while closing popup")}
      window.removeEventListener('message', handleMessage);
      // @ts-ignore - RN dispatches custom event
      document.removeEventListener('message', handleMessage);
      cleanupRef.current = null;
    };

    const checkClosed = isReactNativeWebView
      ? null
      : setInterval(() => {
          if (popup.closed) {
            if (settled) {
              clearInterval(checkClosed as any);
              return;
            }
            cleanup();
            callbacksRef.current.onError?.('Login was cancelled');
          }
        }, 1000);

    const handleMessage = (event: MessageEvent) => {
      if (settled) return;
      if (event.origin && event.origin !== window.location.origin) return;
      const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;

      if (data.type === 'OAUTH_SUCCESS') {
        const returnedState = data.state as string | undefined;
        if (expectedStateRef.current && returnedState && returnedState !== expectedStateRef.current) {
          cleanup();
          callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
          return;
        }
        cleanup();
        const { access_token } = data;
        handleMicrosoftSuccess({ access_token });
      } else if (data.type === 'OAUTH_ERROR') {
        cleanup();
        callbacksRef.current.onError?.(data.error || 'Microsoft login failed');
      }
    };

    window.addEventListener('message', handleMessage);
    // @ts-ignore - RN dispatches custom event
    document.addEventListener('message', handleMessage);

    cleanupRef.current = cleanup;
  }, [handleMicrosoftSuccess]);

  const cancelOAuth = useCallback(() => {
    if (cleanupRef.current) cleanupRef.current();
  }, []);

  // Picks up the access_token stashed by the callback page after the iOS redirect
  // flow. Call this from a mount effect on pages that initiate Microsoft login.
  // Returns true if a pending result was found and handled.
  const processRedirectResult = useCallback(
    (
      callbacks?: {
        onSuccess?: (_message?: string) => void;
        onError?: (_error: string) => void;
      }
    ): boolean => {
      if (typeof window === 'undefined') return false;

      let raw: string | null = null;
      try {
        raw = sessionStorage.getItem(SESSION_KEY_RESULT);
      } catch {
        return false;
      }
      if (!raw) return false;

      let expectedState: string | null = null;
      let storedUserType: string | null = null;
      try {
        sessionStorage.removeItem(SESSION_KEY_RESULT);
        expectedState = sessionStorage.getItem(SESSION_KEY_STATE);
        storedUserType = sessionStorage.getItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_STATE);
        sessionStorage.removeItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_FLOW);
        sessionStorage.removeItem(SESSION_KEY_RETURN_TO);
      } catch {
        /* ignore storage failures */
      }

      if (callbacks) callbacksRef.current = callbacks;

      if (storedUserType) {
        const ut = parseInt(storedUserType, 10);
        if (!Number.isNaN(ut)) userTypeRef.current = ut;
      }

      let parsed: { access_token?: string; state?: string; error?: string };
      try {
        parsed = JSON.parse(raw);
      } catch {
        callbacksRef.current.onError?.('Failed to process Microsoft sign-in result.');
        return true;
      }

      if (parsed.error) {
        callbacksRef.current.onError?.(parsed.error);
        return true;
      }

      if (expectedState && parsed.state && parsed.state !== expectedState) {
        callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
        return true;
      }

      if (!parsed.access_token) {
        callbacksRef.current.onError?.('No access token received from Microsoft');
        return true;
      }

      handleMicrosoftSuccess({ access_token: parsed.access_token });
      return true;
    },
    [handleMicrosoftSuccess]
  );

  // Create a function that sets userType and returns the login function
  const microsoftLoginWithUserType = useCallback((userType: number, onSuccess?: (_message?: string) => void, onError?: (_error: string) => void) => {
    userTypeRef.current = userType;
    // Store the callbacks for later use
    callbacksRef.current = { onSuccess, onError };
    // Return a function that can be called to trigger the login
    return () => {
      microsoftLogin();
    };
  }, [microsoftLogin]);


  return {
    microsoftLogin,
    microsoftLoginWithUserType,
    handleMicrosoftSuccess,
    cancelOAuth,
    processRedirectResult,
  };
};
